import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stepper, Step, StepLabel } from "@mui/material";
import Step2Settings from "./Step2_Settings";
import Step3InviteTeam from "./Step3_InviteTeam";
import Step4Success from "./Step4_Success";
import api from "../../services/api";
import "./OnboardingWizard.css";

export interface WizardState {
  settings: {
    timezone: string;
    currency: string;
    language: string;
  };
  teamInvites: Array<{
    email: string;
    name: string;
    role: string;
  }>;
  createdMembers: Array<{
    id: number;
    email: string;
    name: string;
    role: string;
    tempPassword: string;
  }>;
}

const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation("auth");
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const STEPS = [
    t("onboarding.wizard.steps.settings"),
    t("onboarding.wizard.steps.inviteTeam"),
    t("onboarding.wizard.steps.allSet"),
  ];

  const [wizardState, setWizardState] = useState<WizardState>({
    settings: { timezone: "UTC", currency: "EUR", language: "en" },
    teamInvites: [],
    createdMembers: [],
  });

  const updateState = (updates: Partial<WizardState>) => {
    setWizardState((prev) => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    setError("");
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError("");
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleInviteTeam = async (invites: WizardState["teamInvites"]) => {
    if (invites.length === 0) {
      handleNext();
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = (await api.onboarding.inviteTeam(invites)) as {
        success: boolean;
        created: WizardState["createdMembers"];
        error?: string;
        message?: string;
      };
      if (response.success) {
        updateState({ teamInvites: invites, createdMembers: response.created });
        handleNext();
      } else {
        setError(
          response.error ||
            response.message ||
            t("onboarding.inviteTeam.errors.inviteFailed"),
        );
      }
    } catch {
      setError(t("onboarding.inviteTeam.errors.inviteFailed"));
      handleNext();
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    setError("");
    try {
      await api.onboarding.complete(wizardState.settings);
      navigate("/dashboard");
    } catch {
      setError(t("onboarding.errors.settingsFailed"));
      setTimeout(() => navigate("/dashboard"), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step2Settings
            data={wizardState.settings}
            onUpdate={(data) => updateState({ settings: data })}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <Step3InviteTeam
            invites={wizardState.teamInvites}
            onInvite={handleInviteTeam}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <Step4Success
            createdMembers={wizardState.createdMembers}
            onComplete={handleComplete}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-wizard-container">
      <Box sx={{ width: "100%", maxWidth: 720, mx: "auto" }}>
        <div className="onboarding-wizard-header">
          <img
            src="/hirefocuz-logo.png"
            alt="HireFocuZ"
            className="onboarding-wizard-logo"
          />
          <h1>{t("onboarding.wizard.title")}</h1>
          <p>{t("onboarding.wizard.subtitle")}</p>
        </div>

        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          {renderStep()}
        </Paper>
      </Box>
    </div>
  );
};

export default OnboardingWizard;
